import React, {
  useCallback, KeyboardEvent, useRef, useEffect, useState,
} from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Theme,
  useMediaQuery,
  Typography,
  Grid,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import { useTheme } from '@material-ui/styles';
import clsx from 'clsx';
import { customModalClasses } from './types';

export const useStyles = makeStyles(({ palette, colors, breakpoints }: Theme) => createStyles({
  contentWrapper: {
    width: '100%',
    paddingBottom: '54px',
    '&:first-child': {
      paddingTop: '70px',
    },
    [breakpoints.down('xs')]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  actionsWrapper: {
    justifyContent: 'center',
    paddingBottom: '54px',
    [breakpoints.down('xs')]: {
      justifyContent: 'normal',
      flexDirection: 'column',
      paddingBottom: '10px',
    },
  },
  modalTitle: {
    fontSize: 24,
    color: '#263245',
    fontFamily: 'Montserrat',
    marginBottom: '25px',
    maxWidth: '394px',
  },
  modalText: {
    fontSize: 16,
    maxWidth: '370px',
    marginBottom: 0,
  },
  button: {
    textTransform: 'unset',
    paddingTop: '4px',
    paddingBottom: '4px',
    width: '190px',

    '&:not(:first-child)': {
      marginLeft: '24px',
    },
    [breakpoints.down('xs')]: {
      display: 'block',
      width: '100%',

      '&:not(:first-child)': {
        marginLeft: 0,
        marginTop: '10px',
      },
    },
  },
  continueAction: {
    color: '#018694',
    borderColor: '#018694',
  },
  cancelAction: {
    color: '#F8221A',
    borderColor: '#F8221A',
  },
}), { name: 'ListenChangeRoute' });

type Props = {
  open: boolean;
  cancelHandler: () => void;
  approveHandler: () => void;
  title: string | string[];
  message: string;
  approveText: string;
  cancelText: string;
  onEscapeKeyDown?: () => void;
  focusCancelButton?: boolean;
  customClasses?: customModalClasses;
};

const ModalPreventForm = ({
  open,
  focusCancelButton = false,
  cancelHandler, approveHandler,
  title, message, approveText, cancelText,
  onEscapeKeyDown = () => {},
  customClasses,
}: Props) => {
  const theme = useTheme<Theme>();
  const classes = useStyles();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const approveRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [focusApprove, setFocusApprove] = useState(false);

  const refActionsContainer = useRef();

  // For set ripple on mouse click
  useEffect(() => {
    if (open && approveRef.current) {
      const event = new window.KeyboardEvent('keydown', { key: 'Tab' });
      const target = approveRef.current;

      if (target !== null) {
        target.dispatchEvent(event);
      }
    }
  }, [open, approveRef]);


  useEffect(() => {
    if (open && approveRef.current && cancelRef.current) {
      if (focusCancelButton) {
        setFocusApprove(false);
        cancelRef.current.focus();
      } else {
        setFocusApprove(true);
        approveRef.current.focus();
      }
    }
  }, [open, approveRef, cancelRef, focusCancelButton]);

  const keypressHandler = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onEscapeKeyDown();
      e.stopPropagation();
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      if (focusApprove) {
        if (cancelRef.current) cancelRef.current.focus();
      } else
      if (approveRef.current) approveRef.current.focus();


      setFocusApprove((bool) => !bool);
    }
  }, [cancelRef, approveRef, setFocusApprove, focusApprove, onEscapeKeyDown]);

  return (
    <Dialog
      fullScreen={fullScreen}
      disableBackdropClick
      open={open}
      style={{ zIndex: 2147483102 }}
      onKeyDown={keypressHandler}
      disablePortal
    >
      <DialogContent className={clsx(classes.contentWrapper, customClasses && customClasses.wrapper)}>
        <Grid container alignItems="center" justify="center">
          <Grid item>
            {Array.isArray(title)
              ? (
                <>
                  {title.map((item,i) => (
                    <span key={i}>
                      <Typography
                        gutterBottom
                        variant="h4"
                        align="center"
                        className={clsx(classes.modalTitle, customClasses && customClasses.title)}
                      >
                        {item}
                      </Typography>
                    </span>
                  ))}
                </>
              )
              : (
                <Typography
                  gutterBottom
                  variant="h4"
                  align="center"
                  className={clsx(classes.modalTitle, customClasses && customClasses.title)}
                >
                  {title}
                </Typography>
              )}
          </Grid>
          <Grid item>
            <Typography
              gutterBottom
              variant="body1"
              align="center"
              className={clsx(classes.modalText, customClasses && customClasses.text)}
            >
              {message}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions ref={refActionsContainer} className={clsx(classes.actionsWrapper, customClasses && customClasses.actionWrapper)}>
        <Button
          tabIndex={-1}
          buttonRef={cancelRef}
          onClick={cancelHandler}
          className={clsx(classes.cancelAction, classes.button, customClasses && customClasses.cancelButton)}
          variant="outlined"
          focusRipple
        >
          {cancelText}
        </Button>

        <Button
          tabIndex={-1}
          buttonRef={approveRef}
          onClick={approveHandler}
          className={clsx(classes.continueAction, classes.button, customClasses && customClasses.approveButton)}
          variant="outlined"
          focusRipple
        >
          {approveText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalPreventForm;
